import { useRouter } from "@tanstack/react-router";
import {
  CheckCircle2,
  CircleHelp,
  Clock,
  Database,
  History,
  Loader2,
  RefreshCw,
  Save,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface TicketSyncRow {
  id: string;
  status: string;
  type: string;
  ticketsCreated: number;
  ticketsUpdated: number;
  ticketsArchived: number;
  ticketsErrored: number;
  errorMessage: string | null;
  startedAt: string | number;
  completedAt: string | number | null;
}

interface TicketSyncSettings {
  intervalMs: number;
  lastSyncedAt: string | null;
  schedulerActive: boolean;
  backfillCompletedAt: string | null;
}

function formatRelativeTime(ts: string | number | null): string {
  if (!ts) return "Never";
  const date = typeof ts === "number" ? new Date(ts * 1000) : new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function formatDuration(startedAt: string | number, completedAt: string | number | null): string {
  if (!completedAt) return "—";
  const start = typeof startedAt === "number" ? startedAt * 1000 : new Date(startedAt).getTime();
  const end =
    typeof completedAt === "number" ? completedAt * 1000 : new Date(completedAt).getTime();
  const ms = end - start;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

interface TicketSyncSectionProps {
  initialSettings: TicketSyncSettings;
  initialHistory: TicketSyncRow[];
  totalTickets: number;
}

export function TicketSyncSection({
  initialSettings,
  initialHistory,
  totalTickets,
}: TicketSyncSectionProps) {
  const router = useRouter();
  const [settings, setSettings] = useState(initialSettings);
  const [history, setHistory] = useState(initialHistory);
  const [localTicketCount, setLocalTicketCount] = useState(totalTickets);
  const [isSyncing, setIsSyncing] = useState(false);
  const [backfillConfirming, setBackfillConfirming] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState(
    String(Math.round(initialSettings.intervalMs / 60_000)),
  );
  const [savingInterval, setSavingInterval] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshData = useCallback(async () => {
    const [historyRes, statusRes, countRes] = await Promise.all([
      fetch("/api/tickets/sync-history"),
      fetch("/api/settings/ticket-sync"),
      fetch("/api/tickets/count"),
    ]);
    // Check res.ok before .json() — error responses are plain text
    if (historyRes.ok) setHistory((await historyRes.json()).results ?? []);
    if (statusRes.ok) setSettings(await statusRes.json());
    if (countRes.ok) setLocalTicketCount((await countRes.json()).total ?? 0);
  }, []);

  // Poll while syncing
  useEffect(() => {
    if (!isSyncing) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch("/api/tickets/sync-status");
        if (!res.ok) {
          // Session expired or server error — stop polling instead of spinning forever
          setIsSyncing(false);
          toast.error("Lost connection while syncing", {
            description: `Status check failed (HTTP ${res.status}). Reload the page to check sync state.`,
          });
          return;
        }
        const data = await res.json();
        if (!data.isRunning) {
          setIsSyncing(false);
          await refreshData();
          if (data.sync?.status === "success") {
            toast.success("Ticket sync complete", {
              description: `${data.sync.ticketsCreated} created, ${data.sync.ticketsUpdated} updated`,
            });
          } else if (data.sync?.status === "error") {
            toast.error("Ticket sync failed", {
              description: data.sync.errorMessage || "Unknown error",
            });
          }
          router.invalidate();
        }
      } catch {
        // continue polling
      }
    }, 2000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isSyncing, refreshData, router]);

  async function handleFullSync() {
    setBackfillConfirming(false);
    await handleSync("full");
  }

  async function handleSync(type: "delta" | "full") {
    setIsSyncing(true);
    try {
      const res = await fetch("/api/tickets/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type }),
      });
      if (!res.ok) throw new Error("Failed to start sync");
      const label = type === "full" ? "Full" : "Delta";
      toast.info(`${label} sync started`, {
        description: "Running in background...",
      });
    } catch {
      setIsSyncing(false);
      toast.error("Failed to start sync");
    }
  }

  async function handleSaveInterval() {
    const ms = Number(intervalMinutes) * 60_000;
    if (Number.isNaN(ms) || ms < 60_000) {
      toast.error("Interval must be at least 1 minute");
      return;
    }
    setSavingInterval(true);
    try {
      await fetch("/api/settings/ticket-sync", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intervalMs: ms }),
      });
      setSettings((prev) => ({ ...prev, intervalMs: ms, schedulerActive: true }));
      toast.success("Sync interval updated");
    } catch {
      toast.error("Failed to update interval");
    } finally {
      setSavingInterval(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Status cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Local Tickets</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{localTicketCount.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">mirrored from HubSpot</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Synced</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {settings.lastSyncedAt ? formatRelativeTime(settings.lastSyncedAt) : "Never"}
            </div>
            <p className="text-xs text-muted-foreground">
              {settings.schedulerActive
                ? `Auto-sync every ${Math.round(settings.intervalMs / 60_000)} min`
                : "Scheduler inactive"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-medium">Sync Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <TooltipProvider>
            <div className="grid grid-cols-1 gap-px rounded-lg border bg-border sm:grid-cols-2">
              {/* Delta Sync */}
              <div className="flex items-center justify-between gap-4 rounded-t-lg bg-card p-4 sm:rounded-l-lg sm:rounded-tr-none">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10">
                    <RefreshCw className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium">Delta Sync</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <CircleHelp className="h-3.5 w-3.5 text-muted-foreground/60 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[240px]">
                          Pulls only tickets modified since the last sync. Fast and lightweight —
                          runs automatically on a schedule.
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <p className="text-xs text-muted-foreground">Recent changes only</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => handleSync("delta")} disabled={isSyncing}>
                  {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}
                </Button>
              </div>

              {/* Full Sync */}
              <div className="flex items-center justify-between gap-4 rounded-b-lg bg-card p-4 sm:rounded-r-lg sm:rounded-bl-none">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
                    <History className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium">Full Sync</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <CircleHelp className="h-3.5 w-3.5 text-muted-foreground/60 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px]">
                          Pulls every ticket from HubSpot (no record limit). Use to true-up your
                          local mirror. Takes a few minutes.
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    {settings.backfillCompletedAt ? (
                      <p className="text-xs text-emerald-600">
                        Last run{" "}
                        {new Date(settings.backfillCompletedAt).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">All tickets, no limit</p>
                    )}
                  </div>
                </div>
                {!backfillConfirming ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setBackfillConfirming(true)}
                    disabled={isSyncing}
                  >
                    {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}
                  </Button>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" onClick={handleFullSync} disabled={isSyncing}>
                      Confirm
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground"
                      onClick={() => setBackfillConfirming(false)}
                      disabled={isSyncing}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TooltipProvider>
        </CardContent>
      </Card>

      {/* Sync interval setting */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Schedule</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ticketSyncInterval" className="text-xs">
                Sync interval (minutes)
              </Label>
              <Input
                id="ticketSyncInterval"
                type="number"
                min="1"
                className="w-32"
                value={intervalMinutes}
                onChange={(e) => setIntervalMinutes(e.target.value)}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSaveInterval}
              disabled={savingInterval}
            >
              {savingInterval ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sync history table */}
      <div>
        <h3 className="mb-3 text-sm font-medium">Sync History</h3>
        {history.length === 0 ? (
          <div className="rounded-md border py-8 text-center">
            <p className="text-sm text-muted-foreground">No syncs have been run yet.</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="text-xs uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Created</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Updated</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Archived</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Errors</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Duration</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">
                        {row.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {row.status === "success" ? (
                        <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Success
                        </span>
                      ) : (
                        <div>
                          <span className="flex items-center gap-1.5 text-sm text-destructive">
                            <XCircle className="h-3.5 w-3.5" />
                            Error
                          </span>
                          {row.errorMessage && (
                            <p
                              className="mt-0.5 text-xs text-muted-foreground max-w-[200px] truncate"
                              title={row.errorMessage}
                            >
                              {row.errorMessage}
                            </p>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{row.ticketsCreated}</TableCell>
                    <TableCell className="text-sm">{row.ticketsUpdated}</TableCell>
                    <TableCell className="text-sm">{row.ticketsArchived}</TableCell>
                    <TableCell className="text-sm">
                      {row.ticketsErrored > 0 ? (
                        <span className="text-destructive">{row.ticketsErrored}</span>
                      ) : (
                        row.ticketsErrored
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDuration(row.startedAt, row.completedAt)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatRelativeTime(row.completedAt ?? row.startedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
